import * as XLSX from 'xlsx';
import toast from 'react-hot-toast';

export const ExportService = {
  exportToExcel: (
    organizerId: string, 
    links: any[], 
    stats: any,
    organizers: Record<string, any>,
    pilgrims: Record<string, any>,
    guides: Record<string, any>,
    translations: any,
    financeConfig: any
  ) => {
    try {
      const organizerName = organizers[organizerId]?.name || organizerId;
      const hasBonusApplied = stats.totalPilgrims >= financeConfig.bonusThreshold && financeConfig.bonusAmount > 0;
      const baseAmount = financeConfig.baseAmount;
      const bonusAmount = financeConfig.bonusAmount;
      const notOwnerAmount = financeConfig.notOwnerAmount;
      const amountPerPilgrim = hasBonusApplied ? baseAmount + bonusAmount : baseAmount;
      
      // Create main sheet with pilgrim details
      const rows = links.map(link => {
        // Get pilgrim details
        const pilgrimData = pilgrims[link.pilgrimId] || {};
        
        return {
          [translations?.organizerFinance?.pilgrimName || 'Pilgrim Name']: pilgrimData.pilgrimName || link.pilgrimName || '-',
          [translations?.organizerFinance?.passportNumber || 'Passport Number']: pilgrimData.passportNumber || link.passportNumber || '-',
          [translations?.organizerFinance?.assignedGuide || 'Assigned Guide']: guides[link.guideId]?.name || link.guideName || '-',
          [translations?.organizerFinance?.paymentType || 'Payment Type']: link.isOwner === false ? 'Not Owner' : 'Full Payment',
          [translations?.organizerFinance?.amount || 'Amount']: link.isOwner === false ? notOwnerAmount : amountPerPilgrim
        };
      });

      // Create summary data for separate sheet
      const summaryData = [
        {
          'Category': translations?.organizerFinance?.summary || 'Summary',
          'Value': ''
        },
        {
          'Category': translations?.organizerFinance?.totalPilgrims || 'Total Pilgrims',
          'Value': stats.totalPilgrims
        },
        {
          'Category': translations?.organizerFinance?.regularPilgrims || 'Regular Pilgrims',
          'Value': stats.regularPilgrims
        },
        {
          'Category': translations?.organizerFinance?.notOwnerPilgrims || 'Not Owner Pilgrims',
          'Value': stats.notOwnerPilgrims
        },
        {
          'Category': translations?.organizerFinance?.bonusAmount || 'Bonus Amount',
          'Value': stats.bonusAmount
        },
        {
          'Category': translations?.organizerFinance?.totalAmount || 'Total Amount',
          'Value': stats.totalAmount
        },
        {
          'Category': '',
          'Value': ''
        },
        {
          'Category': translations?.organizerFinance?.configInfo || 'Configuration Info',
          'Value': ''
        },
        {
          'Category': translations?.organizerFinance?.baseAmount || 'Base Amount',
          'Value': baseAmount
        },
        {
          'Category': translations?.organizerFinance?.bonusPerPilgrim || 'Bonus Per Pilgrim',
          'Value': bonusAmount
        },
        {
          'Category': translations?.organizerFinance?.notOwnerAmount || 'Not Owner Amount',
          'Value': notOwnerAmount
        }
      ];

      // Create the main workbook
      const wb = XLSX.utils.book_new();
      
      // Create and append the main details sheet
      const ws = XLSX.utils.json_to_sheet(rows);
      XLSX.utils.book_append_sheet(wb, ws, translations?.organizerFinance?.title || 'Financial Report');

      // Create and append the summary sheet
      const summaryWs = XLSX.utils.json_to_sheet(summaryData);
      XLSX.utils.book_append_sheet(wb, summaryWs, translations?.organizerFinance?.summarySheet || 'Summary');

      const date = new Date().toISOString().split('T')[0];
      XLSX.writeFile(wb, `financial-report-${organizerName}-${date}.xlsx`);

      toast.success(translations?.organizerFinance?.exportSuccess || 'Financial report exported successfully');
    } catch (error) {
      console.error('Error exporting data:', error);
      toast.error(translations?.organizerFinance?.exportError || 'Failed to export financial report');
    }
  },
  
  exportAllToExcel: (
    organizerStats: Record<string, any>,
    organizers: Record<string, any>,
    pilgrims: Record<string, any>,
    guides: Record<string, any>,
    links: Record<string, any>,
    translations: any,
    financeConfigs: Record<string, any>
  ) => {
    try {
      const wb = XLSX.utils.book_new();
      
      // Create overall summary sheet
      const summaryData = [
        {
          'Organizer': translations?.organizerFinance?.totalSummary || 'Total Summary',
          'Total Pilgrims': 0,
          'Regular Pilgrims': 0,
          'Not Owner Pilgrims': 0,
          'Total Amount': 0,
          'Bonus Amount': 0
        }
      ];
      
      let globalTotalPilgrims = 0;
      let globalRegularPilgrims = 0; 
      let globalNotOwnerPilgrims = 0;
      let globalTotalAmount = 0;
      let globalBonusAmount = 0;

      // Prepare data for combined pilgrim data sheet
      const allPilgrims: any[] = [];

      // Process each organizer
      for (const [organizerId, stats] of Object.entries(organizerStats)) {
        const organizerName = organizers[organizerId]?.name || organizerId;
        const config = financeConfigs[organizerId] || financeConfigs['default'];
        
        // Get organizer-specific links
        const organizerLinks = Object.values(links).filter((link: any) => link.organizerId === organizerId);
        
        // Add to global totals
        globalTotalPilgrims += stats.totalPilgrims;
        globalRegularPilgrims += stats.regularPilgrims;
        globalNotOwnerPilgrims += stats.notOwnerPilgrims;
        globalTotalAmount += stats.totalAmount;
        globalBonusAmount += stats.bonusAmount;
        
        // Add row to summary
        summaryData.push({
          'Organizer': organizerName,
          'Total Pilgrims': stats.totalPilgrims,
          'Regular Pilgrims': stats.regularPilgrims,
          'Not Owner Pilgrims': stats.notOwnerPilgrims,
          'Total Amount': stats.totalAmount,
          'Bonus Amount': stats.bonusAmount
        });
        
        // Add details for each pilgrim to the combined data
        if (organizerLinks.length > 0) {
          organizerLinks.forEach((link: any) => {
            const pilgrimData = pilgrims[link.pilgrimId] || {};
            const hasBonusApplied = stats.totalPilgrims >= config.bonusThreshold && config.bonusAmount > 0;
            const amountPerPilgrim = hasBonusApplied ? config.baseAmount + config.bonusAmount : config.baseAmount;
            
            allPilgrims.push({
              'Organizer Name': organizerName,
              'Organizer ID': organizerId,
              [translations?.organizerFinance?.pilgrimName || 'Pilgrim Name']: pilgrimData.pilgrimName || link.pilgrimName || '-',
              [translations?.organizerFinance?.passportNumber || 'Passport Number']: pilgrimData.passportNumber || link.passportNumber || '-',
              [translations?.organizerFinance?.assignedGuide || 'Assigned Guide']: guides[link.guideId]?.name || link.guideName || '-',
              [translations?.organizerFinance?.paymentType || 'Payment Type']: link.isOwner === false ? 'Not Owner' : 'Full Payment',
              [translations?.organizerFinance?.amount || 'Amount']: link.isOwner === false ? config.notOwnerAmount : amountPerPilgrim
            });
          });
        }
      }
      
      // Update summary totals
      summaryData[0] = {
        'Organizer': translations?.organizerFinance?.totalSummary || 'Total Summary',
        'Total Pilgrims': globalTotalPilgrims,
        'Regular Pilgrims': globalRegularPilgrims,
        'Not Owner Pilgrims': globalNotOwnerPilgrims,
        'Total Amount': globalTotalAmount,
        'Bonus Amount': globalBonusAmount
      };
      
      // Create and add combined pilgrim data sheet
      if (allPilgrims.length > 0) {
        const pilgrimsWs = XLSX.utils.json_to_sheet(allPilgrims);
        XLSX.utils.book_append_sheet(wb, pilgrimsWs, translations?.organizerFinance?.allPilgrims || 'All Pilgrims');
      }
      
      // Add summary sheet
      const summaryWs = XLSX.utils.json_to_sheet(summaryData);
      XLSX.utils.book_append_sheet(wb, summaryWs, translations?.organizerFinance?.overview || 'Overview');
      
      // Export the config as another sheet
      const configData = Object.entries(financeConfigs).map(([id, config]: [string, any]) => ({
        'Organizer ID': config.organizerId,
        'Organizer Name': config.organizerName || organizers[config.organizerId]?.name || config.organizerId,
        'Base Amount': config.baseAmount,
        'Bonus Threshold': config.bonusThreshold,
        'Bonus Amount': config.bonusAmount,
        'Not Owner Amount': config.notOwnerAmount || 0
      }));
      
      const configWs = XLSX.utils.json_to_sheet(configData);
      XLSX.utils.book_append_sheet(wb, configWs, translations?.organizerFinance?.configSheet || 'Configuration');
      
      // Save the workbook
      const date = new Date().toISOString().split('T')[0];
      XLSX.writeFile(wb, `financial-report-all-organizers-${date}.xlsx`);

      toast.success(translations?.organizerFinance?.exportAllSuccess || 'All financial data exported successfully');
    } catch (error) {
      console.error('Error exporting all data:', error);
      toast.error(translations?.organizerFinance?.exportAllError || 'Failed to export all financial data');
    }
  }
};
